<template>
  <div class="p-0">
    <h1 class="text-2xl font-bold mb-6">Solicitar Video</h1>

    <div class="bg-gray-100 p-6 rounded-md shadow-md">
      <div class="flex flex-col space-y-4">

        <!-- Select Turno -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="shift">Turno</label>
          <select
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            id="shift"
            name="shift"
            v-model="selectedShift"
            required
          >
            <option value="">Seleccione un turno</option>
            <option
              v-for="shift in shifts.filter(s => s && s.id && s.name)"
              :value="shift.id"
              :key="shift.id"
            >
              {{ shift.name }}
            </option>
          </select>
        </div>

        <!-- Select Curso -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="course">Curso</label>
          <select
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            id="course"
            name="course"
            v-model="selectedCourse"
            required
          >
            <option value="">Seleccione un curso</option>
            <option
              v-for="course in courses.filter(c => c && c.id && c.course)"
              :value="course.id"
              :key="course.id"
            >
              {{ course.course }} - {{ course.division }}
            </option>
          </select>
        </div>

        <!-- Select Asignatura -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="subject">Asignatura</label>
          <select
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            id="subject"
            name="subject"
            v-model="selectedSubject"
            required
          >
            <option value="">Seleccione una asignatura</option>
            <option
              v-for="subject in subjects.filter(s => s && s.id && s.name)"
              :value="subject.id"
              :key="subject.id"
            >
              {{ subject.name }}
            </option>
          </select>
        </div>

        <!-- Select Video Request Type -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="videoType">Tipo de Solicitud</label>
          <select
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            id="videoType"
            name="videoType"
            v-model="formData.video_request_type_id"
            required
          >
            <option value="">Seleccione un tipo</option>
            <option
              v-for="type in videoRequestTypes.filter(t => t && t.id && t.name)"
              :value="type.id"
              :key="type.id"
            >
              {{ type.name }}
            </option>
          </select>
        </div>

        <!-- Title Input -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="title">Título del Video</label>
          <input
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            type="text"
            id="title"
            name="title"
            v-model="formData.title"
            placeholder="Ej: Introducción a las Ecuaciones Lineales"
            required
          >
        </div>

        <!-- Description Textarea -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="description">Descripción</label>
          <textarea
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            id="description"
            name="description"
            v-model="formData.description"
            rows="4"
            placeholder="Describe el contenido del video que solicitas..."
            required
          ></textarea>
        </div>

        <!-- YouTube URL Input -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="youtubeUrl">URL de YouTube (Opcional)</label>
          <input
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            type="url"
            id="youtubeUrl"
            name="youtubeUrl"
            v-model="formData.youtube_url"
            placeholder="https://www.youtube.com/watch?v=..."
          >
        </div>

        <!-- Tags Input -->
        <div class="flex flex-col">
          <label class="text-gray-700 mb-2" for="tags">Etiquetas (Separadas por comas)</label>
          <input
            class="w-full mt-2 border-gray-200 rounded-md focus:border-indigo-600 focus:ring focus:ring-opacity-40 focus:ring-indigo-500"
            type="text"
            id="tags"
            name="tags"
            v-model="tagsInput"
            placeholder="matemática, ecuaciones, primer año, álgebra"
          >
          <div class="mt-2 flex flex-wrap gap-2">
            <span
              v-for="(tag, index) in formData.tags.filter(tag => tag && tag.trim())"
              :key="`tag-${index}-${tag}`"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {{ tag }}
              <button
                type="button"
                @click="removeTag(tag)"
                class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500"
              >
                ×
              </button>
            </span>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {{ errorMessage }}
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {{ successMessage }}
        </div>

        <!-- Buttons -->
        <div class="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          <button
            @click="showConfirmAction"
            :disabled="loading || !isFormValid"
            class="px-4 py-2 text-gray-200 bg-indigo-600 rounded-md hover:bg-indigo-700 focus:outline-none focus:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="loading">Enviando...</span>
            <span v-else>Enviar Solicitud</span>
          </button>
          <button
            @click="resetForm"
            type="button"
            class="px-4 py-2 text-gray-200 bg-gray-500 rounded-md hover:bg-gray-600 focus:outline-none focus:bg-gray-600"
          >
            Limpiar Formulario
          </button>
        </div>

        <!-- ComponentsConfirAction : Modal de Confirmacion -->
        <ConfirmAction
          :isVisible="isVisibleModal"
          @confirm="confirmSubmit"
          @cancel="closeConfirmAction"
          title='¿Quieres enviar la solicitud de video?'
          action='Enviar'
          description="Al presionar aceptar, la solicitud se enviará permanentemente"
        />

      </div>
    </div>
  </div>
</template>

<script>
import ConfirmAction from '../components/commons/ConfirmAction.vue'

export default {
  name: "RequestVideo",
  components: {
    ConfirmAction
  },
  data() {
    return {
      loading: false,
      shifts: [],
      courses: [],
      subjects: [],
      videoRequestTypes: [],
      selectedShift: null,
      selectedCourse: null,
      selectedSubject: null,
      tagsInput: '',
      isVisibleModal: false,
      errorMessage: '',
      successMessage: '',
      formData: {
        subject_id: '',
        class_id: '',
        video_request_type_id: '',
        title: '',
        description: '',
        youtube_url: '',
        tags: []
      }
    }
  },
  computed: {
    isFormValid() {
      return this.selectedShift &&
             this.selectedCourse &&
             this.selectedSubject &&
             this.formData.video_request_type_id &&
             this.formData.title.trim() &&
             this.formData.description.trim()
    },
    isModalContext() {
      // Check if we have listeners for request-submitted event (indicates modal usage)
      return this.$listeners && this.$listeners['request-submitted']
    }
  },
  created() {
    this.loadInitialData()
  },
  watch: {
    tagsInput(newValue) {
      if (newValue && newValue.includes(',')) {
        const newTags = newValue.split(',').map(tag => tag.trim()).filter(tag => tag && tag.length > 0)
        this.formData.tags = [...new Set([...this.formData.tags, ...newTags])].filter(tag => tag && tag.trim())
        this.tagsInput = ''
      }
    },
    selectedShift() {
      this.getCourses()
    },
    selectedCourse() {
      this.getSubjects()
    }
  },
  methods: {
    async loadInitialData() {
      try {
        await Promise.all([
          this.getShifts(),
          this.getVideoRequestTypes()
        ])
      } catch (error) {
        console.error('Error loading initial data:', error)
        this.errorMessage = 'Error al cargar los datos iniciales'
      }
    },

    async getSubjects() {
      try {
        if (this.selectedCourse) {
          this.selectedSubject = null
          const response = await this.$http.get(`/subjects-by-class_id/${this.selectedCourse}`)
          this.subjects = response.data.data || []

          // Asegurar que subjects es siempre un array
          if (!Array.isArray(this.subjects)) {
            this.subjects = []
          }
        } else {
          this.subjects = []
          this.selectedSubject = null
        }
      } catch (error) {
        console.error('Error fetching subjects:', error)
        this.subjects = []
        this.selectedSubject = null
      }
    },

    async getShifts() {
      try {
        const response = await this.$http.get('/shifts')
        this.shifts = response.data.data || []

        // Asegurar que shifts es siempre un array
        if (!Array.isArray(this.shifts)) {
          this.shifts = []
        }
      } catch (error) {
        console.error('Error fetching shifts:', error)
        this.shifts = []
        this.errorMessage = 'Error al cargar los turnos'
      }
    },

    async getCourses() {
      try {
        this.courses = []
        this.selectedCourse = null
        this.selectedSubject = null

        if (this.selectedShift) {
          const response = await this.$http.get(`/classes/${this.selectedShift}`)
          this.courses = response.data.data || []

          // Asegurar que courses es siempre un array
          if (!Array.isArray(this.courses)) {
            this.courses = []
          }
        } else {
          this.courses = []
        }
      } catch (error) {
        console.error('Error fetching courses:', error)
        this.courses = []
        this.selectedCourse = null
        this.selectedSubject = null
      }
    },

    async getVideoRequestTypes() {
      try {
        const response = await this.$http.get('/video-request-types')
        this.videoRequestTypes = response.data.data || response.data
      } catch (error) {
        console.error('Error fetching video request types:', error)
        // Si no existe el endpoint, creamos tipos por defecto
        this.videoRequestTypes = [
          { id: 1, name: 'Explicación de Conceptos' },
          { id: 2, name: 'Ejercicios Resueltos' },
          { id: 3, name: 'Resumen de Tema' },
          { id: 4, name: 'Otro' }
        ]
      }
    },

    removeTag(tagToRemove) {
      this.formData.tags = this.formData.tags.filter(tag => tag && tag !== tagToRemove)
    },

    showConfirmAction() {
      if (!this.isFormValid) {
        this.errorMessage = 'Por favor, complete todos los campos obligatorios'
        return
      }
      this.isVisibleModal = true
    },

    closeConfirmAction() {
      this.isVisibleModal = false
    },

    async confirmSubmit() {
      this.closeConfirmAction()
      await this.submitForm()
    },

    async submitForm() {
      if (!this.isFormValid) {
        this.errorMessage = 'Por favor, complete todos los campos obligatorios'
        return
      }

      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const payload = {
          subject_id: this.selectedSubject,
          class_id: this.selectedCourse,
          video_request_type_id: this.formData.video_request_type_id,
          title: this.formData.title,
          description: this.formData.description,
          youtube_url: this.formData.youtube_url,
          tags: this.formData.tags.filter(tag => tag && tag.trim())
        }

        await this.$http.post('/video-requests', payload)

        this.successMessage = 'Solicitud de video enviada exitosamente'

        // Emit event to parent component first
        this.$emit('request-submitted')

        // Reset form after a short delay to show success message
        setTimeout(() => {
          this.resetForm()
        }, 1500)

        // Redirigir después de 2 segundos solo si no estamos en modal
        // if (!this.isModalContext) {
        //   setTimeout(() => {
        //     this.$router.push('/home')
        //   }, 2000)
        // }

      } catch (error) {
        console.error('Error submitting video request:', error)
        this.errorMessage = error.response?.data?.message || 'Error al enviar la solicitud'
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.selectedShift = null
      this.selectedCourse = null
      this.selectedSubject = null
      this.courses = []
      this.subjects = []
      this.formData = {
        subject_id: '',
        class_id: '',
        video_request_type_id: '',
        title: '',
        description: '',
        youtube_url: '',
        tags: []
      }
      this.tagsInput = ''
      this.errorMessage = ''
      this.successMessage = ''
    }
  }
}
</script>

<style scoped>
/* Add specific styles if needed */
</style>